import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Download, Pencil, Trash2 } from "lucide-react";
import { api, errorMessage, fieldErrors, formatDate } from "@/lib/api";
import { download } from "@/lib/admin";
import { validateFields, applySpinRules } from "@/lib/validators";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { ConfigField } from "@/components/form/ConfigField";
import { SpinCard } from "@/components/form/SpinSelect";
import { ErrorSummary, Notice } from "@/components/form/FormFeedback";
import { Loading } from "@/components/layout/RequireAuth";
import { ReportButtons } from "@/components/admin/ReportButtons";
import { usePageTitle } from "@/components/layout/AppLayout";
import { useRegistrationConfig, spinHintFor } from "@/pages/Register";
import { ScoreSummary, AnswersByStep } from "@/components/assessment/ScoreSummary";
import { PageHead } from "@/pages/admin/AdminLayout";
import { TempPasswordDialog } from "@/components/admin/TempPasswordDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const show = (f, v, config) => {
    if (!v) return "—";
    if (f.type === "province") return config.provinces.find((p) => p.sigla === v)?.name || v;
    if (f.type === "spin") return config.spins.find((s) => s.id === v)?.name || v;
    return v;
};

export default function AziendaDetail() {
    usePageTitle("Dettaglio azienda");
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const { config } = useRegistrationConfig();
    const [c, setC] = useState(null);
    const [error, setError] = useState("");
    const [editing, setEditing] = useState(false);
    const [values, setValues] = useState({});
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState(null);
    const isSuper = can(user, "sposta_aziende", "C");
    const canEdit = can(user, "aziende", "M");
    const canDelete = canEdit && can(user, "elimina_aziende", "C");
    const canDownload = can(user, "download", "C");

    useEffect(() => { api.get(`/admin/companies/${id}`).then(({ data }) => setC(data)).catch((e) => setError(errorMessage(e))); }, [id]);
    if (error) return <Notice kind="error" role="alert" testId="company-detail-error">{error}</Notice>;
    if (!c || !config) return <Loading />;
    const fields = config.fields.filter((f) => f.in_profile);

    const onChange = (k, v) => setValues((s) => ({ ...s, [k]: v, ...(k === "provincia" && isSuper ? { spin_id: applySpinRules(config.spins, v) } : {}) }));
    const save = async (e) => {
        e.preventDefault();
        const errs = validateFields(fields.filter((f) => isSuper || f.key !== "spin_id"), values);
        setErrors(errs);
        if (Object.keys(errs).length) return;
        try {
            const { data } = await api.put(`/admin/companies/${id}`, { values });
            setC({ ...c, ...data.company });
            setEditing(false);
            setStatus({ kind: "success", text: data.message });
        } catch (err) {
            const fe = fieldErrors(err);
            if (fe) setErrors(fe); else setStatus({ kind: "error", text: errorMessage(err) });
        }
    };
    const remove = async () => {
        await api.delete(`/admin/companies/${id}`);
        navigate("/amministrazione/aziende");
    };
    const name = c.profile.ragione_sociale;

    return (
        <>
            <PageHead title={name}>
                <div className="flex flex-wrap gap-2">
                    <ReportButtons section="azienda" extra={{ id }} />
                    {canEdit && c.profile?.email && <TempPasswordDialog endpoint={`/admin/companies/${id}/temp-password`} email={c.profile.email} onDone={setStatus} testPrefix="company-temp" />}
                    {canDownload && <button type="button" className="btn-secondary py-2" onClick={() => download(`/admin/companies/${id}/pdf`)} data-testid="company-pdf-button"><Download aria-hidden="true" size={18} /> Scarica PDF</button>}
                    {canDelete && (
                        <AlertDialog>
                            <AlertDialogTrigger asChild><button type="button" className="btn-primary py-2" data-testid="company-delete-button"><Trash2 aria-hidden="true" size={18} /> Elimina azienda</button></AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader><AlertDialogTitle>Elimina azienda</AlertDialogTitle>
                                    <AlertDialogDescription data-testid="company-delete-dialog-text">{`Eliminare definitivamente ${name}? L'operazione non è reversibile.`}</AlertDialogDescription></AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel className="btn-tertiary border-0 h-auto mt-0" data-testid="company-delete-cancel">Annulla</AlertDialogCancel>
                                    <AlertDialogAction onClick={remove} className="rounded-lg border-0 bg-edi-error text-edi-surface hover:bg-edi-error hover:opacity-90" data-testid="company-delete-confirm">Elimina</AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    )}
                </div>
            </PageHead>
            {status && <div className="mb-4"><Notice kind={status.kind} testId="admin-company-save-status">{status.text}</Notice></div>}
            <div className="grid gap-6 xl:grid-cols-[1.6fr_1fr] items-start">
                <section className="edi-card p-5" aria-labelledby="ad-dati">
                    <div className="flex justify-between gap-3 flex-wrap"><h2 id="ad-dati" className="text-xl font-bold">Dati aziendali</h2>
                        {!editing && canEdit && <button type="button" className="btn-secondary py-2" onClick={() => { setValues({ ...c.profile }); setErrors({}); setStatus(null); setEditing(true); }} data-testid="admin-company-edit-button"><Pencil aria-hidden="true" size={16} /> Modifica</button>}</div>
                    {editing ? (
                        <form noValidate onSubmit={save} className="mt-4 space-y-4" data-testid="admin-company-edit-form">
                            <ErrorSummary errors={errors} fields={fields} />
                            <div className="grid gap-4 sm:grid-cols-2">
                                {fields.map((f) => (
                                    <div key={f.key} className={f.type === "spin" ? "sm:col-span-2" : ""}>
                                        <ConfigField field={f} value={values[f.key]} error={errors[f.key]} onChange={onChange} provinces={config.provinces} spins={config.spins}
                                            readOnly={f.type === "spin" && !isSuper} spinHint={spinHintFor(config.spins, values.provincia)} />
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-3"><button type="submit" className="btn-primary" data-testid="admin-company-save-button">Salva</button>
                                <button type="button" className="btn-secondary" onClick={() => setEditing(false)} data-testid="admin-company-cancel-button">Annulla</button></div>
                        </form>
                    ) : (
                        <dl className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="admin-company-data">
                            {fields.map((f) => <div key={f.key} className="min-w-0"><dt className="text-sm text-edi-muted">{f.label}</dt><dd className="font-medium break-words" data-testid={`admin-company-value-${f.key}`}>{show(f, c.profile[f.key], config)}</dd></div>)}
                        </dl>
                    )}
                </section>
                <aside className="space-y-4">
                    <div className="edi-card p-5"><h2 className="text-lg font-bold mb-3">Sede SPIN</h2><SpinCard spin={c.spin} /></div>
                    <div className="edi-card p-5" data-testid="admin-company-status">
                        <h2 className="text-lg font-bold mb-2">Stato</h2>
                        <p>Consenso AI: <strong>{c.ai_consent?.given ? `espresso il ${formatDate(c.ai_consent.updated_at)}` : "non espresso"}</strong></p>
                        <p>Email: <strong>{c.email_confirmed ? "confermata" : "non confermata"}</strong></p>
                        <p data-testid="admin-company-last-access">Ultimo accesso: <strong>{c.accessi?.ultimo_accesso ? new Date(c.accessi.ultimo_accesso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "mai"}</strong></p>
                        <p data-testid="admin-company-access-30d">Accessi ultimi 30 giorni: <strong>{c.accessi?.accessi_30_giorni ?? 0}</strong></p>
                        <p className="edi-help">Risposte, punteggi e consenso non sono modificabili dagli amministratori.</p>
                    </div>
                </aside>
            </div>
            <section className="mt-6" aria-labelledby="ad-hist">
                <h2 id="ad-hist" className="text-xl font-bold mb-3">Storico assessment ({c.submissions.length})</h2>
                {c.submissions.length === 0 ? <Notice kind="info">Assessment non ancora compilato</Notice> : c.submissions.map((s, i) => (
                    <details key={s.id} className="edi-card p-4 mb-3" open={i === 0} data-testid={`admin-submission-${i}`}>
                        <summary className="cursor-pointer font-semibold">{formatDate(s.submitted_at)} · {s.score_total}/100 · {s.level} · v{s.questionnaire_version}</summary>
                        <div className="mt-4 space-y-6"><ScoreSummary submission={s} history={c.submissions.slice(c.submissions.indexOf(s))} /><AnswersByStep view={s.answers_view} /></div>
                    </details>
                ))}
            </section>
        </>
    );
}
